import { Emitter } from "@core/events/emitter";

/**
 * Viewport antialiasing and render scale.
 *
 * ## Why the app has no antialiasing without this
 *
 * `WebGLRenderer` is constructed with `antialias: true`, but that is MSAA on
 * the DEFAULT framebuffer and the viewport never draws there — every frame
 * goes through `EffectComposer`, whose render targets are single-sampled. So
 * the flag has been inert since the composer landed, and edges have been raw
 * aliased ever since. It went largely unnoticed until ambient occlusion
 * started drawing a dark line down every one of those edges.
 *
 * MSAA is deliberately not offered as a mode: it would have to be enabled on
 * the composer's targets, and `N8AOPass` renders the scene into its OWN
 * target and samples that target's depth, so MSAA would do nothing at all in
 * the default (AO on) configuration. A control that silently no-ops is worse
 * than no control. The two post-process modes below work in both states.
 *
 * ## Render scale
 *
 * Multiplies the device pixel ratio `EngineService` renders at. Above 1 this
 * is supersampling — the most effective antialiasing available, and the
 * expensive one; below 1 it is the cheapest way to buy frame time back on a
 * weak GPU. It applies to the composer chain as a whole, so AO, outlines and
 * the AA pass all follow it.
 */

export const ANTIALIASING_MODES = ["Off", "FXAA", "SMAA"] as const;
export type AntialiasingMode = (typeof ANTIALIASING_MODES)[number];

export interface RenderQualitySettings {
  readonly antialiasing: AntialiasingMode;
  /** Multiplier on the (already capped) device pixel ratio. */
  readonly renderScale: number;
}

/**
 * FXAA by default, chosen by eye on 2026-08-18 over N8AO's own recommendation
 * of SMAA. The reason SMAA disappointed was the pipeline AS IT WAS: both
 * algorithms threshold their edge detection on luma, and until 2026-09-03 the
 * chain fed them UN-ENCODED LINEAR color (`COLOR_PIPELINE.md`), which
 * compresses contrast in the dark end — exactly where AO has just darkened
 * everything. ⚠ That premise is GONE: the `OutputPass` now sits before both
 * AA passes, so they see display-encoded colour, which is what they were
 * designed for. The default is left where it was, not re-litigated; SMAA is
 * worth re-measuring on the same frame before anyone cites this paragraph as
 * the reason to keep FXAA. As measured then, SMAA was the
 * more selective of the two and so finds less to fix; measured against AA off
 * on the same frame it moved a third as many bytes as FXAA. Its extra
 * precision is wasted when the input is what limits it, and the blunter
 * instrument wins.
 */
export const RENDER_QUALITY_DEFAULTS: RenderQualitySettings = {
  antialiasing: "FXAA",
  renderScale: 1,
};

export const RENDER_SCALE_RANGE = { min: 0.5, max: 2, step: 0.05 } as const;

/**
 * Ceiling on `devicePixelRatio × renderScale`. The DPR itself is already
 * capped at 2, so without this a 2× display at scale 2 would allocate 4×
 * linear (16× the pixels) across the composer's targets, N8AO's targets and
 * SMAA's — enough to lose the WebGL context outright on modest hardware.
 */
export const MAX_EFFECTIVE_PIXEL_RATIO = 3;
/** Floor, so a mis-set scale cannot collapse the buffer to nothing. */
export const MIN_EFFECTIVE_PIXEL_RATIO = 0.25;

const K_PREF_KEY = "Campfire.Dev.RenderQuality";

export class RenderQualityModel {
  readonly onChanged = new Emitter<RenderQualitySettings>();

  private m_settings: RenderQualitySettings = readPref();

  get settings(): RenderQualitySettings {
    return this.m_settings;
  }

  update(patch: Partial<RenderQualitySettings>): void {
    const next = clampSettings({ ...this.m_settings, ...patch });
    if (next.antialiasing === this.m_settings.antialiasing && next.renderScale === this.m_settings.renderScale) {
      return;
    }
    this.m_settings = next;
    writePref(next);
    this.onChanged.emit(next);
  }
}

export const RENDER_QUALITY_MODEL = Symbol.for("Campfire.RenderQualityModel");

function clampSettings(s: RenderQualitySettings): RenderQualitySettings {
  return {
    antialiasing: ANTIALIASING_MODES.includes(s.antialiasing)
      ? s.antialiasing
      : RENDER_QUALITY_DEFAULTS.antialiasing,
    renderScale: Number.isFinite(s.renderScale)
      ? Math.min(RENDER_SCALE_RANGE.max, Math.max(RENDER_SCALE_RANGE.min, s.renderScale))
      : RENDER_QUALITY_DEFAULTS.renderScale,
  };
}

function readPref(): RenderQualitySettings {
  try {
    const raw = window.localStorage.getItem(K_PREF_KEY);
    if (!raw) return RENDER_QUALITY_DEFAULTS;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return RENDER_QUALITY_DEFAULTS;
    return clampSettings({
      ...RENDER_QUALITY_DEFAULTS,
      ...(parsed as Partial<RenderQualitySettings>),
    });
  } catch {
    return RENDER_QUALITY_DEFAULTS;
  }
}

function writePref(settings: RenderQualitySettings): void {
  try {
    if (
      settings.antialiasing === RENDER_QUALITY_DEFAULTS.antialiasing &&
      settings.renderScale === RENDER_QUALITY_DEFAULTS.renderScale
    ) {
      window.localStorage.removeItem(K_PREF_KEY);
    } else {
      window.localStorage.setItem(K_PREF_KEY, JSON.stringify(settings));
    }
  } catch {
    /* private-mode storage refusal is not worth surfacing */
  }
}
